import { RolloutState } from './rolloutState';

/**
 * How widely a feature is open, independent of any one account.
 *
 * - `off`      nobody, staff included
 * - `internal` staff only, ignoring every grant
 * - `granted`  whoever holds the grant
 * - `on`       everybody
 */
export type FeatureState = 'off' | 'internal' | 'granted' | 'on';

export type FeatureSwitchState = FeatureState | RolloutState;

export const FEATURE_STATES: readonly FeatureState[] = ['off', 'internal', 'granted', 'on'] as const;

/**
 * The state of a feature the switch row says nothing about.
 *
 * `off`, so that forgetting a feature closes it rather than opens it: a grant
 * handed out before anyone wrote the switch entry reaches nobody until the
 * entry exists.
 *
 * The cost is that a feature is inert until it is listed, which makes the
 * switch row a registry rather than an override — every new flag needs an entry
 * to do anything at all.
 *
 * It also means the read path cannot be introduced over an empty row without
 * darkening every feature the fleet already grants. Whichever change first
 * reads this row must seed it with the features in use, at `granted`.
 */
export const DEFAULT_FEATURE_STATE: FeatureState = 'off';

export function isFeatureState(value: unknown): value is FeatureState {
  return typeof value === 'string' && (FEATURE_STATES as readonly string[]).includes(value);
}
